// src/pages/admin/forms/TransportForm.jsx
// Create / edit / view form for a transport record (name + GST).
import { useState, useEffect } from "react";
import { B, fonts, Input, Btn } from "../../../components/UI";
import { getGSTValidationMessage } from "../../../utils/validation";
import { normalizeTransportName, normalizeTransportGst, toTitleCase } from "../../../utils/transport";

const blank = () => ({ name: "", gst: "", status: "active" });

export default function TransportForm({ initial, onDone, onSubmit, saving, readOnly = false, showToast }) {
  const [form, setForm] = useState(() => (initial ? { ...blank(), ...initial } : blank()));
  const [gstError, setGstError] = useState("");

  useEffect(() => {
    setForm(initial ? { ...blank(), ...initial } : blank());
    setGstError("");
  }, [initial]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = () => {
    if (readOnly) return;
    if (!String(form.name || "").trim()) {
      showToast?.("Transport name is required", B.red);
      return;
    }
    const message = getGSTValidationMessage(form.gst);
    if (message) {
      setGstError(message);
      showToast?.(message, B.red);
      return;
    }
    onSubmit?.({ ...form, name: normalizeTransportName(form.name), gst: normalizeTransportGst(form.gst) });
  };

  const labelStyle = { display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body };
  const inputStyle = { background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 13px", color: B.text, fontSize: 13, outline: "none", boxSizing: "border-box", width: "100%", fontFamily: fonts.body };

  return (
    <div style={{ background: B.orangeFaint, border: `1.5px solid ${B.orangeMid}`, borderRadius: 14, padding: 24, marginBottom: 24 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color: B.text, marginBottom: 20, fontFamily: fonts.adminDisplay }}>
        {readOnly ? "👁️ View Transport" : initial ? "✏️ Edit Transport" : "➕ Add Transport"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
        {/* Title Case while typing (whitespace is fully normalized on submit) */}
        <Input label="Transport Name" value={form.name || ""} onChange={e => set("name", toTitleCase(e.target.value))} disabled={readOnly} />
        <div>
          <label style={labelStyle}>GST Number (Optional)</label>
          <input
            value={form.gst || ""}
            onChange={e => { const v = normalizeTransportGst(e.target.value); set("gst", v); setGstError(getGSTValidationMessage(v)); }}
            disabled={readOnly}
            placeholder="e.g. 24ABCDE1234F1Z5"
            style={inputStyle}
          />
          {gstError && !readOnly && (
            <div style={{ fontSize: 11, color: B.red, marginTop: 4, fontFamily: fonts.body }}>{gstError}</div>
          )}
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <select
            value={form.status || "active"}
            onChange={e => set("status", e.target.value)}
            disabled={readOnly}
            style={inputStyle}
          >
            <option value="active">active</option>
            <option value="deleted">deleted</option>
          </select>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
        <Btn onClick={onDone} variant="ghost">Close</Btn>
        {!readOnly && (
          <Btn onClick={handleSubmit} disabled={saving} variant="primary">
            {saving ? "Saving..." : initial ? "Save Transport" : "Add Transport"}
          </Btn>
        )}
      </div>
    </div>
  );
}